import React from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { useClientActivePackages } from '../../../../../../hooks/client.hooks'
import { AlertHoverCard } from '../AlertHoverCard'
import type { AlertHoverPayload, PackagesAlertBody } from '../alertHover.types'

type Props = {
  clientId: string
  onOpenAlerts?: () => void
}

/**
 * Self-fetching packages hover content.
 * Rendered lazily inside a Tooltip so the fetch only fires on hover.
 */
const PackagesHoverContent: React.FC<Props> = ({ clientId, onOpenAlerts }) => {
  const { data, isLoading } = useClientActivePackages(clientId)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={16} />
      </Box>
    )
  }

  const packages = data?.clientSiteActivePackages ?? []

  if (packages.length === 0) {
    return null
  }

  type PackageItem = {
    id: string
    siteId: string | null
    clientId: string | null
    creditsConsumed: number
    creditsTotal: number
    lastUsedOn: string | null
    packageDefinition: { name: string; type: string }
    site: { name: string } | null
    client: { v1Id: string | null } | null
  }

  const body: PackagesAlertBody = {
    membership: (packages as PackageItem[])
      .filter((p) => p.packageDefinition?.type === 'UNLIMITED')
      .map((p) => ({
        name: p.packageDefinition?.name ?? 'Membership',
        used: p.creditsConsumed ?? 0,
      })),
    credits: (packages as PackageItem[])
      .filter((p) => p.packageDefinition?.type !== 'UNLIMITED')
      .map((p) => ({
        label: p.packageDefinition?.name ?? 'Package',
        left: (p.creditsTotal ?? 0) - (p.creditsConsumed ?? 0),
      })),
  }

  const payload: AlertHoverPayload = {
    id: clientId,
    type: 'packages',
    body,
  }

  return <AlertHoverCard data={payload} onOpenAlerts={onOpenAlerts} />
}

export default PackagesHoverContent
